<i18n src="./resources/locales.json"></i18n>
<template>
  <v-alert
    ref="newMsgAlert"
    v-if="showNotification"
    :class="{
      'sms-notification-bg-high-contrast': isHighContrastThemeActive,
      'primary': !isHighContrastThemeActive
    }"
    class="sms-new-notification-container"
    :outlined="isHighContrastThemeActive"
    transition="slide-y-reverse-transition"
    dismissible
    dense
  >
    <template v-slot:close="{ toggle }">
      <v-btn
        icon
        small
        class="sms-new-notification-close-btn"
        @click.stop="toggle(); closeNotification();"
      >
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </template>
    <v-card
      :class="{
        'sms-notification-bg-high-contrast': isHighContrastThemeActive,
        'primary': !isHighContrastThemeActive
      }"
      flat
      tile
      dark
      @click="goToMessage"
    >
      <SmsListItem :show-actions="false">
        <template v-slot:avatar>
          <SmsAvatar :contact="newNotification.contact" />
        </template>
        <template v-slot:title>
          <div :title="newNotification.displayName" :style="CSS_VARS">
            <span class="name">{{ newNotification.displayName }}</span>
            <span class="timestamp">{{ getMessageTimeFormatted(newNotification.lastMessageTime) }}</span>
          </div>
        </template>
        <template v-slot:subtitle>
          <div class="message">{{ newNotification.text }}</div>
        </template>
      </SmsListItem>
    </v-card>
    <v-divider />
    <template v-if="!showQuickMessage">
      <v-row no-gutters>
        <v-btn dark text small @click="closeNotification">
          {{ $t('dismiss') }}
        </v-btn>
        <v-spacer />
        <v-btn dark text small @click="replyToMessage">
          <v-icon>mdi-reply</v-icon>
          {{ $t('reply') }}
        </v-btn>
      </v-row>
    </template>
    <template v-if="showQuickMessage">
      <v-list-item class="pr-2">
        <v-list-item-content>
          <SmsTextFieldComponent ref="messageInput" quick-message dense @sendMessage="sendQuickMessage" />
        </v-list-item-content>
        <v-list-item-action class="ma-0 ml-2">
          <v-btn dark icon @click="sendQuickMessage">
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>
    </template>
  </v-alert>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ThemeMixin from '@/mixins/ThemeMixin';
import TimeFormatMixin from '@/mixins/TimeFormatMixin';
import SmsListItem from '@/components/SmsListItem';
import SmsAvatar from '@/components/SmsAvatar/SmsAvatar';
import SmsTextFieldComponent from '@/components/SmsConversation/SmsFooterComponents/SmsTextFieldComponent';
import SmsNotification from '@/classes/SmsNotification';
import Message from '@/classes/v2/Message';
import store from '@/store';

export default {
  name: 'SmsNotification',

  mixins: [TimeFormatMixin, ThemeMixin],

  components: { SmsAvatar, SmsListItem, SmsTextFieldComponent },

  data() {
    return {
      showQuickMessage: false,
      notificationTimeout: null,
      showNotification: false,
      maxOpenTime: 10000,
      notificationSoundAudioBuffer: { context: null, sourceNode: null },
      newNotification: {},
      quickReplyMessage: {}
    };
  },

  computed: {
    ...mapGetters('user', ['currentUser']),
    ...mapGetters('notifications', ['getLastNotification']),
    ...mapGetters('preferences', ['hasNotificationsEnable', 'selectedIncomingNotificationSound']),
    ...mapGetters('conversationsV2', ['getUnreadConversations', 'activeConversation']),

    CSS_VARS() {
      let timestampWidth = 60; // default width for hours format (h:mm A)
      const dateComposition = this.getMessageTimeFormatted(this.newNotification.lastMessageTime).split('/');
      if (dateComposition.length === 2) {
        timestampWidth = 40; // width for date format (M/D)
      } else if (dateComposition.length === 3) {
        timestampWidth = 80; // width for date and year format (M/D/YYYY)
      }
      return {
        '--timestamp-width': `${timestampWidth}px`
      };
    },

    getNotifications() {
      const notifications = [];
      this.getUnreadConversations.forEach(conversation => {
        notifications.push(new SmsNotification(conversation));
      });
      return notifications;
    }
  },

  watch: {
    getUnreadConversations(newValue) {
      if (!document.hidden && this.hasNotificationsEnable && newValue.length > 0) {
        this.openNotification();
      }
      this.setNotificationIconTab();
    }
  },

  methods: {
    ...mapActions('conversationsV2', ['setActiveConversationId', 'sendSMS', 'clearUnreadMessagesForNumber']),

    setNotificationIconTab() {
      const currentAppleFavicon = document.getElementById('apple-favicon');
      const currentMediumFavicon = document.getElementById('medium-favicon');
      const currentSmallFavicon = document.getElementById('small-favicon');
      const currentFavicon = document.getElementById('favicon');
      if (this.getNotifications.length > 0) {
        currentAppleFavicon.href = 'favicons/apple-notification-touch-icon.png';
        currentMediumFavicon.href = 'favicons/favicon-notification-32x32.png';
        currentSmallFavicon.href = 'favicons/favicon-notification-16x16.png';
        currentFavicon.href = 'favicons/favicon-notification.ico';
      } else {
        currentAppleFavicon.href = 'favicons/apple-touch-icon.png';
        currentMediumFavicon.href = 'favicons/favicon-32x32.png';
        currentSmallFavicon.href = 'favicons/favicon-16x16.png';
        currentFavicon.href = 'favicons/favicon.ico';
      }
    },

    openNotification() {
      const currentUser = store.getters['user/currentUser'];
      this.getNotifications.forEach(newNotification => {
        if (newNotification.text !== this.quickReplyMessage.body &&
          newNotification.lastMessageTime !== this.quickReplyMessage.originationTime &&
          this.getLastNotification.lastMessageTime !== newNotification.lastMessageTime &&
          newNotification.number !== currentUser.number ) {
          this.newNotification = newNotification;
          if (this.notificationTimeout) {
            clearTimeout(this.notificationTimeout);
          }
          this.notificationTimeout = setTimeout(() => {
            this.showNotification = false;
          }, this.maxOpenTime);

          this.showNotification = true;
          this.quickReplyMessage = {};
          this.$notificationSoundManager.playNotificationSound(
            this.selectedIncomingNotificationSound,
            this.notificationSoundAudioBuffer
          );
        }
      });
    },

    closeNotification() {
      clearTimeout(this.notificationTimeout);
      this.showNotification = false;
      this.showQuickMessage = false;
    },

    goToMessage() {
      this.showNotification = false;
      this.setActiveConversationId(this.newNotification.number);
    },

    replyToMessage() {
      this.showQuickMessage = !this.showQuickMessage;
      clearTimeout(this.notificationTimeout);
    },

    sendQuickMessage() {
      if (!this.$refs.messageInput.messageText) return;

      this.showQuickMessage = false;
      this.showNotification = false;
      this.quickReplyMessage = new Message({
        from: this.currentUser.number,
        to: this.newNotification.number,
        body: this.$refs.messageInput.messageText,
        originationTime: Date.now()
      });
      this.$refs.messageInput.messageText = '';
      this.sendSMS(this.quickReplyMessage);
      this.clearUnreadMessagesForNumber(this.newNotification.number);
    }
  }
};
</script>

<style>
.sms-new-notification-close-btn {
  position: absolute;
  top: -5px;
  right: -5px;
}

.sms-new-notification-container {
  position: fixed;
  right: 10px;
  bottom: 10px;
  width: 250px;
  z-index: 100;
  padding: 5px 10px;
}
.sms-notification-bg-high-contrast {
  background-color: var(--v-surface-base) !important;
}

.sms-new-notification-container .v-alert__dismissible {
  top: 15px !important;
  right: 5px !important;
  position: absolute !important;
}
.sms-new-notification-container .v-btn--icon.v-size--small .v-icon {
  font-size: 0.9rem !important;
  font-weight: bolder;
}

.sms-new-notification-container .v-list-item {
  padding: 0px !important;
}
.sms-new-notification-container .sms-contact-container {
  width: 230px;
}
.sms-new-notification-container .sms-contact-container:hover {
  background: transparent;
}
.sms-new-notification-container .sms-contact-container:hover .sms-contact-title,
.sms-new-notification-container .sms-contact-container:hover .sms-contact-subtitle {
  color: inherit !important;
}

.sms-new-notification-container .sms-contact-title .name {
  float: left;
  width: calc(100% - var(--timestamp-width) - 15px); /* 15px from margins in timestamp */
  font-size: 0.9rem !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sms-new-notification-container .sms-contact-title .timestamp {
  float: right;
  width: var(--timestamp-width);
  margin-left: 5px;
  margin-right: 10px;
  font-size: 0.75rem !important;
  text-align: right;
}
.sms-new-notification-container .sms-contact-subtitle .message {
  float: left;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
